import { useState, useEffect, useCallback } from "react";
import { ThumbsUp, ThumbsDown, Bookmark } from "lucide-react";
import { toggleThumb } from "../../../../api/feed";
import { useRightSidebar } from "../../../contexts/RightSidebarContext";
import { useDark, getTheme } from "../../../Universal/useNavTheme";
import ArticleDetailPanel from "../../../Universal/ArticleCardDetailPanel";
import ShareArticleButton from "../../../Universal/ShareArticleButton";
import { tileCardStyle, tileHoverHandlers } from "../utils";

const APP_BLUE = "#1c9dea";

const ActionBtn = ({ active, label, onClick, t, children }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    try {
      if (navigator.vibrate) navigator.vibrate(10);
    } catch {}
    onClick?.(e);
  };

  return (
    <button
      type="button"
      aria-pressed={!!active}
      aria-label={label}
      onClick={handleClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: "50%",
        border: `1px solid ${active ? "rgba(28,157,234,0.5)" : t.cardBorder}`,
        background: active ? "rgba(28,157,234,0.08)" : "transparent",
        cursor: "pointer",
        transition: "all 0.2s",
        flexShrink: 0,
        padding: 0,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "rgba(28,157,234,0.4)";
          e.currentTarget.style.background = "rgba(28,157,234,0.05)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = t.cardBorder;
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      {children}
    </button>
  );
};

const iconStyle = (active) => ({
  color: active ? APP_BLUE : "rgba(148,163,184,0.6)",
  fill: active ? APP_BLUE : "none",
  filter: active ? "drop-shadow(0 0 4px rgba(28,157,234,0.5))" : "none",
  width: 15,
  height: 15,
  transition: "all 0.2s",
});

const TileActionWrapper = ({ article, t, onInteract, children }) => {
  const [localLike, setLocalLike] = useState(article?.like || false);
  const [localDislike, setLocalDislike] = useState(article?.dislike || false);
  const [localCatalog, setLocalCatalog] = useState(article?.catalog || false);

  const dark = useDark();
  const { setRightComponent, setIsRightOpen, setRightSidebarMode } =
    useRightSidebar();

  useEffect(() => {
    setLocalLike(article?.like || false);
  }, [article?.like]);
  useEffect(() => {
    setLocalDislike(article?.dislike || false);
  }, [article?.dislike]);
  useEffect(() => {
    setLocalCatalog(article?.catalog || false);
  }, [article?.catalog]);

  const docId = article?.document_id;

  const handleCardClick = useCallback(() => {
    if (!article) return;
    setRightSidebarMode(null);
    setIsRightOpen(true);
    setRightComponent(
      <ArticleDetailPanel
        article={article}
        cardId={docId}
        t={getTheme(document.documentElement.classList.contains("dark"))}
        onInteract={(cardId, documentId, state) => {
          onInteract?.(documentId, state);
        }}
      />,
    );
  }, [
    article,
    docId,
    setRightComponent,
    setIsRightOpen,
    setRightSidebarMode,
    onInteract,
  ]);

  const handleLikeClick = (e) => {
    const next = !localLike;
    setLocalLike(next);
    toggleThumb({ document_id: docId, action: "like" });
    let newDislike = localDislike;
    if (localDislike) {
      setLocalDislike(false);
      toggleThumb({ document_id: docId, action: "dislike" });
      newDislike = false;
    }
    onInteract?.(docId, {
      like: next,
      dislike: newDislike,
      catalog: localCatalog,
    });
  };

  const handleDislikeClick = (e) => {
    const next = !localDislike;
    setLocalDislike(next);
    toggleThumb({ document_id: docId, action: "dislike" });
    let newLike = localLike;
    if (localLike) {
      setLocalLike(false);
      toggleThumb({ document_id: docId, action: "like" });
      newLike = false;
    }
    onInteract?.(docId, {
      like: newLike,
      dislike: next,
      catalog: localCatalog,
    });
  };

  const handleCatalogClick = (e) => {
    const next = !localCatalog;
    setLocalCatalog(next);
    toggleThumb({ document_id: docId, action: "catalog" });
    onInteract?.(docId, {
      like: localLike,
      dislike: localDislike,
      catalog: next,
    });
  };

  return (
    <div
      onClick={handleCardClick}
      style={tileCardStyle(t)}
      {...tileHoverHandlers(t)}
    >
      {/* Tile content */}
      {children}

      {/* Action bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          paddingTop: 14,
          marginTop: 14,
          borderTop: `1px solid ${t.divider}`,
        }}
      >
        <ActionBtn
          active={localLike}
          label={localLike ? "Unlike" : "Like"}
          onClick={handleLikeClick}
          t={t}
        >
          <ThumbsUp strokeWidth={1.5} style={iconStyle(localLike)} />
        </ActionBtn>

        <ActionBtn
          active={localDislike}
          label={localDislike ? "Remove dislike" : "Dislike"}
          onClick={handleDislikeClick}
          t={t}
        >
          <ThumbsDown strokeWidth={1.5} style={iconStyle(localDislike)} />
        </ActionBtn>

        <ActionBtn
          active={localCatalog}
          label={localCatalog ? "Remove bookmark" : "Bookmark"}
          onClick={handleCatalogClick}
          t={t}
        >
          <Bookmark strokeWidth={1.5} style={iconStyle(localCatalog)} />
        </ActionBtn>

        <ShareArticleButton
          documentId={docId}
          article={article}
          variant="icon"
          iconSize={15}
          strokeWidth={1.5}
        />
      </div>
    </div>
  );
};

export default TileActionWrapper;
